import API_PATHS from '@base/features/api/constants/apiPaths';
import { IApiClient } from '@base/features/api/model/apiClient';
import { MessagesService } from '@base/features/chat/messagesService';
import BaseTable from '@base/features/game/model/baseTable';
import { ITable } from '@base/features/game/model/interfaces';
import BufferReader from '@base/features/socket/bufferReader';
import Codes from '@base/features/socket/codes';
import { ILanguageService } from '@base/features/i18n/model/interfaces';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { BasePreferences } from '@base/features/preferences/model/types';
import { deserialize } from 'object-deserializer';
import { Subject, BehaviorSubject } from 'rxjs';
import { priceDeserializer } from './deserializers';
import {
  AmazonPurchaseData,
  AmazonPurchaseOptions,
  ITicketService,
  Price,
  PurchaseData,
  PurchaseMethod,
  StripeIntent,
} from './interfaces';
import { getEnv } from '@base/utils/helpers';

export default class TicketsService<P extends BasePreferences = BasePreferences, T extends ITable = BaseTable>
  implements ITicketService {
  onTicketsModal: Subject<PurchaseMethod[]> = new Subject();
  accountBalance = new BehaviorSubject(0);

  constructor(
    private apiClient: IApiClient,
    private language: ILanguageService,
    private players: IPlayersOnlineRepository<P, T>,
    private modalProvider: IModalProvider,
    private messageService: MessagesService<P, T> /* private userService: IUserService */
  ) {}

  haveSufficientBalance(table: ITable): boolean {
    const title = this.language.translate(
      'join_table_error_1s_2s',
      this.language.translate('join_l'),
      String(table.getNumber())
    );

    if (this.players.me && !this.players.me.isTicketPlayer()) {
      //аккаунт не тикетный
      this.modalProvider.show({
        title,
        body: this.language.translate('no_tickets_buy_tickets'),
        buttons: [
          { title: this.language.translate('cancel'), onClick: () => this.modalProvider.hide() },
          {
            title: this.language.translate('buy_tickets'),
            onClick: () => {
              this.modalProvider.hide();
              this.openPaymentModal();
            },
          },
        ],
      });
      return false;
    } else if (this.accountBalance.value < table.cost / 2) {
      //недостаточно средств
      this.modalProvider.show({
        title,
        body: this.language.translate('insufficient_tickets'),
        buttons: [
          { title: this.language.translate('cancel'), onClick: () => this.modalProvider.hide() },
          {
            title: this.language.translate('buy_tickets'),
            onClick: () => {
              this.modalProvider.hide();
              this.openPaymentModal();
            },
          },
        ],
      });
      return false;
    } else {
      return true;
    }
  }
  async amazonBuy(data: AmazonPurchaseData): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'amazon_buy',
        price: data.price,
        access_token: data.accessToken,
        reference_id: data.orderReferenceId,
      },
      API_PATHS.api
    );
    const d = await res.json();
    if (d.code === 0) {
      return d;
    } else {
      throw new Error(d.descr);
    }
  }

  async stripeConfirm(id: string): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'stripe_confirm',
        id,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return data;
  }

  async stripeIntent(amount: string): Promise<StripeIntent> {
    const res = await this.apiClient.authPost(
      {
        do: 'stripe_intent',
        amount,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return data;
  }

  async purchase(purchaseData: PurchaseData): Promise<void> {
    const res = await this.apiClient.authPost(
      {
        do: 'purchase',
        ...purchaseData,
      },
      API_PATHS.api
    );
    const { code, descr } = await res.json();
    if (code !== 0) {
      throw new Error(descr);
    }
  }

  async getPaypalToken(): Promise<string> {
    const res = await this.apiClient.post(
      {
        do: 'paypal_account',
      },
      API_PATHS.api
    );
    const data = await res.json();
    if (data.code !== 0 || !data['client_id']) {
      throw new Error('Could not get paypal token');
    }
    return data['client_id'];
  }

  async initStripe() {
    if (process.env.NX_FACEBOOK_GAME) {
      return null;
    } else {
      return (await import('@stripe/stripe-js')).loadStripe('NX_STRIPE_TOKEN');
    }
  }

  async stripeId(amount: string): Promise<string> {
    const res = await this.apiClient.authPost(
      {
        amount: amount,
      },
      API_PATHS.stripe
    );
    const { id } = await res.json();
    return id;
  }

  async amazonPurchase(amount: string): Promise<AmazonPurchaseOptions> {
    const res = await this.apiClient.authPost(
      {
        amount,
        currencyCode: 'USD',
      },
      API_PATHS.amazon
    );

    const options = await res.json();
    return options;
  }

  async getPrices(): Promise<Price[]> {
    const res = await this.apiClient.post({ do: 'get_prices' }, API_PATHS.api);
    const data = await res.json();
    return deserialize(data, (d) => d.required('prices').asArrayOfObjects(priceDeserializer));
  }

  async canBuyTickets() {
    const res = await this.apiClient.authPost(
      {
        do: 'can_buy',
        json: 1,
      },
      API_PATHS.api
    );

    const data = await res.json();
    return data;
  }

  async openPaymentModal() {
    const res = await this.canBuyTickets();
    const platform = getEnv('NX_PLATFORM');
    if (platform === 'WIN') res.options = [PurchaseMethod.WIN];
    if (platform === 'MAC') res.options = [PurchaseMethod.MAC];
    if (res.allowed === 1 && res.options) {
      this.onTicketsModal.next(res.options);
    }
  }

  closePaymentModal() {
    this.onTicketsModal.next(undefined);
  }

  async paypalBuy(price: string) {
    const res = await this.apiClient.authPost(
      {
        do: 'paypal_buy',
        json: 1,
        price,
      },
      API_PATHS.api
    );

    const data = await res.json();
    return data.redirect_url;
  }

  private onReceiveMoney(data: BufferReader) {
    const pid = data.readInt16();
    const money = data.readFloat32();
    const type = data.readInt16();
    const player = this.players.getPlayerOnline(pid);

    if (!player)
      return console.error('Cannot parse money message for player: ' + pid + ' reason: player not found');

    if (this.players.me && player.id !== this.players.me.id)
      return console.error('Received money for other player: ' + pid);

    const newBalance = Math.round(money * 100.0) / 100.0;

    if (!this.accountBalance.value) this.accountBalance.next(newBalance);

    let chatMessage = '';
    switch (type) {
      case Codes.MoneyMessageTypes.WIN:
        chatMessage += this.language.translate(
          'mini_tourney_win',
          String(Math.round((newBalance - this.accountBalance.value) * 100) / 100)
        );
        break;
      case Codes.MoneyMessageTypes.START:
        chatMessage += this.language.translate(
          'chat_message_mini_tourney_fee',
          String(Math.round((this.accountBalance.value - newBalance) * 100) / 100)
        );
        break;
      case Codes.MoneyMessageTypes.ABORT:
        chatMessage += this.language.translate(
          'mini_tourney_refunded',
          String(Math.round((newBalance - this.accountBalance.value) * 100) / 100)
        );
        break;
      default: // for OK and other messages
    }
    chatMessage += this.language.translate('account_balance', newBalance.toString());

    this.messageService.createChatMessage({
      type: Codes.MessageTypes.GOD,
      text: chatMessage,
      // color: this.setColor(player)
    });
    if (this.players.me) {
      const table = this.players.me.getTable();
      if (table) {
        this.messageService.createChatMessage({
          type: Codes.MessageTypes.TABLE,
          text: chatMessage,
          tid: table.getNumber(),
        });
      }
    }
    /* this.userService.updatePersonalInfoValue('acct_value', newBalance); */
    this.accountBalance.next(newBalance);
  }
}
